import TrashOutline from 'assets/svg/icons/TrashOutline';
import classNames from 'classnames';
import { textToDateRange } from 'components/molecules/Fields/DateRangerPicker/helpers';
import dayjs from 'dayjs';
import useWorldTheme from 'hooks/shared/useWorldTheme';
import useFeatures from 'hooks/useFeatures';
import useResetableState from 'hooks/useResetableState';
import { first, last, range } from 'lodash';
import type { FC } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import DatePicker_, {
  type DatePickerProps as DatePickerProps_,
} from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { IColors } from 'theme/colors';
import { getTracingEvents } from 'utils/amplitude';
import { captureLeftRightArrowEvents } from 'utils/captureLeftRightArrowEvents';
import { dateLocales, months } from 'utils/constants';
import { ChevronLeftIconOutline, ChevronRightIconOutline } from 'utils/icons';
import { CalendarIcon } from 'utils/icons/figmaIcons';
import { Button } from '../Button';
import FreeTextInput from '../Form/FreeTextInput';
import './DatePicker.style.scss';
import type { DatePickerProps, DateRangeProps } from './DatePicker.types';

const colorMap: Record<IColors, string> = {
  expenses: 'date-picker-expenses',
  revenues: 'date-picker-revenues',
  taxes: 'date-picker-taxes',
  primary: 'date-picker-primary',
  bank: 'date-picker-bank',
};

const CustomControl = function CustomInput(props: any) {
  const { customInputProps, ...rest } = props;

  return (
    <FreeTextInput
      {...customInputProps}
      {...rest}
      Icon={rest.hideIcon ? null : CalendarIcon}
      wrapperClassName={'w-full'}
    />
  );
};

const dayjsInstance = dayjs();
const maxYear = dayjsInstance.year() + 20;
const minYear = 1900;

const DatePicker: FC<DatePickerProps> = ({
  placeholder,
  iconStyle = {},
  hideIcon,
  iconWrapperClasses = '',
  wrapperClassName = '',
  containerClassName = '',
  customInputProps = {},
  color: propsColor,
  tracing,
  onCalendarOpen,
  onChange,
  compact,
  selected,
  minDate,
  maxDate,
  startDate,
  endDate,
  mergedMonthYearDropdown,
  aiTooltip,
  // it's important not to delete this key "value", as we don't want to pass it to the DatePicker_ component by mistake
  value,
  showClearButton,
  portal,
  ...props
}) => {
  const color = useWorldTheme();

  const colorClass = colorMap[propsColor || color];

  const { t, i18n } = useTranslation();

  const ref = useRef<any>(null);

  const tracingEvents = getTracingEvents(tracing, { onCalendarOpen, onChange });

  const dateFormat = useFeatures('formats.datePicker');

  const getDateValue = useCallback((date: DatePickerProps['selected']) => {
    if (date == null) return;
    if (dayjs(date).isValid()) {
      // fix for timezone issue
      // https://stackoverflow.com/questions/7556591/is-the-javascript-date-object-always-one-day-off
      // when we pass a string like this "2011-09-24" to the date constructor it thinks that this date is in UTC,
      // and then it tries to apply some kind of offset in order to make the date in the user local timezone
      // but apparently dayjs can handle this by default
      return dayjs(date).toDate();
    }
  }, []);

  const selectedValue = useMemo(
    () => getDateValue(selected),
    [selected, getDateValue],
  );
  const minDateValue = useMemo(
    () => getDateValue(minDate),
    [minDate, getDateValue],
  );
  const maxDateValue = useMemo(
    () => getDateValue(maxDate),
    [maxDate, getDateValue],
  );
  const startDateValue = useMemo(
    () => getDateValue(startDate),
    [startDate, getDateValue],
  );
  const endDateValue = useMemo(
    () => getDateValue(endDate),
    [endDate, getDateValue],
  );

  const years = range(
    minDate ? dayjs(minDate).year() : minYear,
    maxDate ? dayjs(maxDate).year() + 1 : maxYear,
    1,
  );

  const MyContainer: DatePickerProps_['calendarContainer'] = useCallback(
    (props: any) => {
      return (
        <div className={props.className}>
          {props.children}
          {showClearButton && (
            <Button
              color="red"
              structure="light"
              className="w-full mt-1"
              disabled={!selectedValue && !startDateValue && !endDateValue}
              onClick={(e) => {
                ref.current?.onClearClick?.(e);
                ref.current?.setOpen?.(false);
              }}
            >
              <TrashOutline className="w-5 h-5" />
              {t('clear')}
            </Button>
          )}
        </div>
      );
    },
    [showClearButton, selectedValue, startDateValue, endDateValue, t],
  );

  const pickerClasses = classNames({
    'date-picker-atom-compact': compact,
    [`date-picker-atom ${colorClass}`]: !compact,
    'date-picker-error': !!props.error,
    'date-picker-selects-range': props.selectsRange,
  });

  const portalId = 'DATE_PICKER_PORTAL';

  return (
    <div
      className={classNames(
        'relative flex items-center flex-1',
        containerClassName,
        portal ? '' : pickerClasses,
      )}
    >
      {portal &&
        createPortal(
          <div id={portalId} className={pickerClasses} />,
          document.body,
        )}
      <DatePicker_
        ref={ref}
        customInput={
          <CustomControl
            hideIcon={hideIcon}
            error={props.error}
            customInputProps={{
              ...customInputProps,
              placeholder,
              aiTooltip,
            }}
          />
        }
        renderCustomHeader={({
          date,
          changeYear,
          changeMonth,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => {
          return (
            <div className="flex justify-around text-primary-700 gap-1 font-semibold">
              {props.showMonthDropdown === false ? null : (
                <div className="flex flex-1 mb-2 flex-row items-center bg-primary-50 rounded-md p-1.5">
                  <Button
                    structure="text"
                    size="sm"
                    color="primary"
                    disabled={prevMonthButtonDisabled}
                    onClick={decreaseMonth}
                  >
                    <div className="border border-primary-100 bg-white rounded-md cursor-pointer">
                      <ChevronLeftIconOutline className="w-4 h-4 m-1" />
                    </div>
                  </Button>

                  {mergedMonthYearDropdown ? (
                    <div className="flex-1 text-center whitespace-nowrap">
                      {`${months[dayjs(date).month()]}, ${dayjs(date).year()}`}
                    </div>
                  ) : (
                    <select
                      className="flex-1 !py-0 px-2 text-center !appearance-none border-none bg-none bg-transparent"
                      value={months[dayjs(date).month()]}
                      onChange={({ target: { value } }) => {
                        changeMonth(months.indexOf(value));
                      }}
                    >
                      {months.map((option) => (
                        <option
                          className="inline-block capitalize"
                          key={option}
                          value={option}
                        >
                          {option}
                        </option>
                      ))}
                    </select>
                  )}
                  <Button
                    structure="text"
                    size="sm"
                    color="primary"
                    disabled={nextMonthButtonDisabled}
                    onClick={increaseMonth}
                  >
                    <div className="border border-primary-100 bg-white rounded-md cursor-pointer">
                      <ChevronRightIconOutline className="w-4 h-4 m-1" />
                    </div>
                  </Button>
                </div>
              )}
              {mergedMonthYearDropdown ? null : (
                <div className="flex flex-1 mb-2 flex-row items-center bg-primary-50 rounded-md p-1.5">
                  <Button
                    structure="text"
                    color="primary"
                    size="sm"
                    disabled={date.getFullYear() - 1 < (first(years) as number)}
                    onClick={() => changeYear(date.getFullYear() - 1)}
                  >
                    <div className="border border-primary-100 bg-white rounded-md cursor-pointer">
                      <ChevronLeftIconOutline className="w-4 h-4 m-1" />
                    </div>
                  </Button>
                  <select
                    className="flex-1 !py-0 px-2 !text-center !appearance-none bg-none border-none bg-transparent"
                    value={Number(date.getFullYear())}
                    onChange={({ target: { value } }) =>
                      changeYear(Number(value))
                    }
                  >
                    {years.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <Button
                    structure="text"
                    color="primary"
                    size="sm"
                    disabled={date.getFullYear() + 1 > (last(years) as number)}
                    onClick={() => changeYear(date.getFullYear() + 1)}
                  >
                    <div className="border border-primary-100 bg-white rounded-md cursor-pointer">
                      <ChevronRightIconOutline className="w-4 h-4 m-1" />
                    </div>
                  </Button>
                </div>
              )}
            </div>
          );
        }}
        locale={dateLocales[i18n.language as keyof typeof dateLocales]}
        dateFormat={dateFormat}
        calendarContainer={MyContainer}
        renderDayContents={(dayNumber: number, dayDate: Date) => {
          return (
            <div className="react-datepicker__day-value w-full h-full relative p-0.5">
              <div className="abs-center">{dayNumber}</div>
            </div>
          );
        }}
        {...props}
        showMonthDropdown={false}
        showYearDropdown={false}
        autoComplete="off"
        selected={selectedValue}
        minDate={minDateValue as Date}
        maxDate={maxDateValue as Date}
        startDate={startDateValue}
        endDate={endDateValue}
        onKeyDown={captureLeftRightArrowEvents}
        yearDropdownItemNumber={props.scrollableYearDropdown ? 100 : 20}
        onCalendarOpen={tracingEvents.onCalendarOpen}
        onChange={tracingEvents.onChange}
        portalId={portal ? portalId : undefined}
      />
    </div>
  );
};

export const DateRange: FC<DateRangeProps> = ({
  value,
  onChange,
  ...props
}) => {
  const { t } = useTranslation();
  const [[startDateNumber, endDateNumber], setDateRange] = useResetableState({
    init: [value?.[0] || null, value?.[1] || null],
    deps: [value],
  });
  return (
    <div className="flex">
      <DatePicker
        customInputProps={{
          ...props.customInputprops,
        }}
        portal={props.portal}
        placeholder={t('datepicker.select_date_range')}
        selectsRange
        selected={startDateNumber}
        startDate={startDateNumber}
        endDate={endDateNumber}
        onChange={(dates: [Date | null, Date | null]) => {
          // const dates = value as unknown as (Date | null)[];
          const date1 = dates?.[0];
          const date2 = dates?.[1];

          setDateRange(dates);

          if (date1 && date2) {
            return onChange(
              dayjs(date1).format('YYYY-MM-DD'),
              dayjs(date2).format('YYYY-MM-DD'),
            );
          }

          if (!date1 && !date2) {
            return onChange(null, null);
          }
        }}
        onChangeRaw={(event) => {
          if (!event) return;
          if (!(event.target instanceof HTMLInputElement)) return;
          const inputValue = event.target.value;
          if (!inputValue) return;
          const range = textToDateRange(inputValue);
          if (!range) return;
          onChange(
            dayjs(range[0]).format('YYYY-MM-DD'),
            dayjs(range[1]).format('YYYY-MM-DD'),
          );
        }}
        minDate={props.start || null}
        maxDate={props.end || null}
      />
    </div>
  );
};

export default DatePicker;
